import { useEffect, useState } from 'react';
import { setConsent, setMarketingConsent, hasMarketingConsent } from '../utils/cookieConsent';

export default function CookieSettingsModal({ open, onClose }) {
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    
    // Load current consent state when modal opens
    setMarketingEnabled(hasMarketingConsent());
    
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleSave = () => {
    setConsent({ marketing: marketingEnabled });
    setMarketingConsent(marketingEnabled);
    // Dispatch event to notify Google Ads hook
    window.dispatchEvent(new Event('cookieConsentChanged'));
    onClose && onClose();
  };

  const handleMarketingToggle = () => {
    setMarketingEnabled(!marketingEnabled);
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.42)' }} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 24, boxShadow: '0 24px 70px rgba(17,24,39,0.18)', border: '1px solid rgba(229,231,235,0.9)' }}>
          <div style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.96)', borderBottom: '1px solid #e5e7eb', padding: '18px 24px', borderTopLeftRadius: 24, borderTopRightRadius: 24, backdropFilter: 'blur(14px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 id="cookie-settings-title" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827', fontFamily: 'var(--font-heading)' }}>Cookie-Einstellungen</h2>
              <button type="button" aria-label="Modal schließen" onClick={onClose} style={{ height: 36, width: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, background: '#f3f4f6', border: '1px solid #e5e7eb', cursor: 'pointer' }}>
                <svg style={{ height: 20, width: 20 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div style={{ padding: '24px 24px' }}>
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: 0, color: '#374151', lineHeight: 1.6 }}>
                Wir verwenden Cookies, um Ihre Einstellungen zu speichern und die Funktionalität unserer Website zu gewährleisten. 
                Funktionale Cookies sind für den Betrieb erforderlich. Marketing-Cookies werden nur mit Ihrer Einwilligung verwendet.
              </p>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: 14 }}>
                Verantwortlich: LRH Software UG (haftungsbeschränkt)
              </p>
            </div>
            
            <div style={{ marginTop: 16, padding: 18, border: '1px solid #e5e7eb', borderRadius: 18, background: 'linear-gradient(180deg, #ffffff, #f9fafb)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>Funktionale Cookies</div>
                  <div style={{ color: '#4b5563', fontSize: 14 }}>Erforderlich für die grundlegenden Funktionen der Website.</div>
                  <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>Speicherdauer: Bis zum Ende der Browser-Sitzung</div>
                </div>
                <span style={{ fontSize: 12, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '5px 10px', borderRadius: 999, fontWeight: 700 }}>Immer aktiv</span>
              </div>
            </div>

            <div style={{ marginTop: 16, padding: 18, border: '1px solid #e5e7eb', borderRadius: 18, background: marketingEnabled ? '#f0fdf4' : 'linear-gradient(180deg, #ffffff, #f9fafb)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>Marketing-Cookies</div>
                  <div style={{ color: '#4b5563', fontSize: 14, marginBottom: 8 }}>
                    Werden für Conversion-Messung und Werbung verwendet (Google Ads, OpenAI Ads).
                  </div>
                  <div style={{ padding: 12, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginTop: 8 }}>
                    <div style={{ fontWeight: 700, color: '#111827', fontSize: 13, marginBottom: 6 }}>Google Ads / OpenAI Ads Conversion Tracking</div>
                    <div style={{ color: '#4b5563', fontSize: 12, lineHeight: 1.5 }}>
                      <div style={{ marginBottom: 4 }}><strong>Zweck:</strong> Conversion-Messung und Werbung</div>
                      <div style={{ marginBottom: 4 }}><strong>Anbieter:</strong> Google Ireland Ltd., OpenAI Ireland Ltd.</div>
                      <div style={{ marginBottom: 4 }}><strong>Verwendete Cookies:</strong> <code style={{ background: '#f3f4f6', padding: '2px 4px', borderRadius: 4, fontSize: 11 }}>_gcl_au</code>, <code style={{ background: '#f3f4f6', padding: '2px 4px', borderRadius: 4, fontSize: 11 }}>__oppref</code>, <code style={{ background: '#f3f4f6', padding: '2px 4px', borderRadius: 4, fontSize: 11 }}>__obref</code> und weitere</div>
                      <div style={{ marginBottom: 4 }}><strong>Speicherdauer:</strong> 90 Tage</div>
                      <div><strong>Datenübertragung:</strong> USA (über Standardvertragsklauseln)</div>
                    </div>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={marketingEnabled}
                    onChange={handleMarketingToggle}
                    style={{ width: 20, height: 20, cursor: 'pointer', marginRight: 8, accentColor: '#16a34a' }}
                  />
                  <span style={{ fontSize: 12, color: marketingEnabled ? '#15803d' : '#6b7280', fontWeight: marketingEnabled ? 700 : 500 }}>
                    {marketingEnabled ? 'Aktiviert' : 'Deaktiviert'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div style={{ position: 'sticky', bottom: 0, background: 'rgba(249,250,251,0.96)', borderTop: '1px solid #e5e7eb', padding: '16px 24px', borderBottomLeftRadius: 24, borderBottomRightRadius: 24, backdropFilter: 'blur(14px)' }}>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={onClose} style={{ padding: '10px 16px', fontSize: 14, fontWeight: 600, color: '#374151', background: '#fff', border: '1px solid #d1d5db', borderRadius: 12, cursor: 'pointer', boxShadow: '0 6px 14px rgba(17,24,39,0.05)' }}>
                Abbrechen
              </button>
              <button onClick={handleSave} style={{ padding: '10px 20px', fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #16a34a, #15803d)', border: '1px solid transparent', borderRadius: 12, cursor: 'pointer', boxShadow: '0 12px 24px rgba(22,163,74,0.2)' }}>
                Einstellungen speichern
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


